import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { transformationsWithTransformOriginAnimated } from '@euler/utils/transformOrigin';
import { BlurTint } from 'expo-blur';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import {
  LayoutRectangle,
  Platform,
  StyleProp,
  StyleSheet,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';
import {
  SharedValue,
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { DropdownContext } from './context';
import {
  DropdownAlignment,
  DropdownBackdropMode,
  DropdownComponentStyles,
  DropdownSelectionMode,
} from './types';
import { mergeComponentStyles } from './util';

const kDefaultMargin = 10;

export type SynthesizedDropdownStyles = ReturnType<
  typeof useSynthesizedDropdownStyles
>;

const useDefaultComponentStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo<DropdownComponentStyles>(() => {
    const styles: DropdownComponentStyles = StyleSheet.create({
      container: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'stretch',
      },
      containerDisabled: {
        opacity: 0.5,
      },
      menuItem: {
        minHeight: rpx(44),
        justifyContent: 'center',
        paddingHorizontal: 10,
        paddingVertical: 8,
      },
      menuItemText: {
        color: '#000',
      },
      inputContainer: {
        flex: 1,
      },
      inputContainerDisabled: {
        backgroundColor: '#f0f0f0',
      },
      input: {
        color: '#000',
      },
      inputDisabled: {
        color: '#ccc',
      },
      focusedInputContainer: {
        borderWidth: rpx(2),
        borderColor: '#2669db',
      },
      dropdownContainer: {
        position: 'absolute',
        borderColor: '#ddd',
        borderWidth: 1,
        backgroundColor: '#fff',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.15,
        shadowRadius: 10,
        elevation: 5,
      },
      dropdown: {},
      dropdownContentContainer: {
        paddingVertical: 0,
      },
      searchInput: {
        padding: rpx(10),
        fontSize: rpx(12),
        borderRadius: rpx(5),
        backgroundColor: '#fff',
        marginHorizontal: rpx(16),
        marginTop: rpx(4),
        marginBottom: rpx(5),
        borderWidth: 0.5,
        borderColor: '#B5BDC4',
      },
      creatable: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: rpx(10),
        paddingVertical: rpx(5),
      },
      creatableInput: {
        flex: 1,
        borderWidth: 1,
        borderColor: '#B5BDC4',
        paddingHorizontal: rpx(10),
        fontSize: rpx(14),
        borderRadius: 5,
      },
      creatableTitle: {
        fontFamily: FontFamily.NotoSans.Regular,
        fontSize: rpx(16),
        color: '#000',
      },
      icon: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: rpx(32),
        justifyContent: 'center',
        alignItems: 'center',
      },
      tag: {
        paddingHorizontal: rpx(8),
        paddingVertical: rpx(2),
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: rpx(1),
        borderColor: '#ddd',
        borderRadius: 80,
        backgroundColor: '#eee',
        marginRight: rpx(5),
        marginTop: rpx(2.5),
        marginBottom: rpx(2.5),
        shadowColor: '#999',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.4,
        shadowRadius: 3,
        elevation: 2,
      },
      tagText: {
        fontFamily: FontFamily.NotoSans.Regular,
        fontSize: rpx(12),
        color: '#000',
      },
    });
    return {
      ...styles,
      caretIcon: {
        size: rpx(21),
        style: {
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          paddingLeft: rpx(4),
          paddingRight: rpx(8),
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
      clearable: {
        size: rpx(7.5),
        color: '#fff',
        style: {
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          paddingHorizontal: rpx(5),
          justifyContent: 'center',
          alignItems: 'center',
        },
        buttonStyle: {
          backgroundColor: '#d0d0d0',
          padding: rpx(4),
        },
      },
      tagRemoveIcon: {
        color: '#eee',
        size: rpx(10),
        style: {
          marginLeft: rpx(5),
          backgroundColor: '#aaa',
          borderRadius: rpx(14),
          width: rpx(14),
          height: rpx(14),
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
    };
  }, [rpx]);
};

export const useSynthesizedDropdownStyles = ({
  mode,
  align,
  open,
  autoFlip,
  query,
  elemBoundingRect,
  animValue,
  matchWidth = true,
  showCaretIcon = true,
  maxDropdownHeight,
  dropdownWidth,
  dropdownHeight,
  withCustomValueRender,
  withClearButton,
  withIcon,
  placeholderTextColor,
  searchInputPlaceholderTextColor,
  backdropMode,
  backdropBlurTint,
  backdropBlurIntensity,
  ...props
}: {
  mode: DropdownSelectionMode;
  align: DropdownAlignment;
  open: boolean;
  autoFlip?: boolean;
  query: string | undefined;
  backdropMode: DropdownBackdropMode | undefined;
  backdropBlurTint: BlurTint | undefined;
  backdropBlurIntensity: number | undefined;
  componentStyles: DropdownComponentStyles | undefined;
  placeholderTextColor: string | undefined;
  searchInputPlaceholderTextColor: string | undefined;
  elemBoundingRect: LayoutRectangle | undefined;
  animValue: SharedValue<number>;
  matchWidth: boolean | undefined;
  maxDropdownHeight: number | undefined;
  dropdownWidth: number | undefined;
  dropdownHeight: number | undefined;
  showCaretIcon: boolean | undefined;
  withCustomValueRender: boolean;
  withClearButton: boolean;
  withIcon: boolean;
}) => {
  const context = useContext(DropdownContext);

  const { top: saTop, bottom: saBottom } = useSafeAreaInsets();

  const queryRef = useRef(query);
  queryRef.current = query;

  const viewport = useWindowDimensions();

  const boundingRect = context?.boundingRect ?? {
    x: 0,
    y: 0,
    width: viewport.width,
    height: viewport.height,
  };

  const defaultComponentStyles = useDefaultComponentStyles();

  const componentStyles = useMemo<DropdownComponentStyles>(
    () =>
      mergeComponentStyles(
        mergeComponentStyles(defaultComponentStyles, context?.componentStyles),
        props.componentStyles,
      ),
    [context?.componentStyles, defaultComponentStyles, props.componentStyles],
  );

  const flattenMenuItemStyle = useMemo(
    () => StyleSheet.flatten(componentStyles.menuItem),
    [componentStyles.menuItem],
  );

  const flattenCaretIconStyle = useMemo(
    () => StyleSheet.flatten(componentStyles.caretIcon?.style),
    [componentStyles.caretIcon?.style],
  );

  const metricSize = (value: any) => (typeof value === 'number' ? value : 0);

  const caretIconContainerWidth =
    typeof flattenCaretIconStyle?.width === 'number'
      ? flattenCaretIconStyle?.width
      : (componentStyles.caretIcon?.size ?? 21) +
        metricSize(
          flattenCaretIconStyle.paddingLeft ??
            flattenCaretIconStyle.paddingHorizontal,
        ) +
        metricSize(
          flattenCaretIconStyle.paddingRight ??
            flattenCaretIconStyle.paddingHorizontal,
        );

  const flattenIconStyle = useMemo(
    () => StyleSheet.flatten(componentStyles.icon),
    [componentStyles.icon],
  );
  const iconWidth =
    typeof flattenIconStyle?.width === 'number' ? flattenIconStyle?.width : 32;

  const preferredDropdownPlacement = useRef<'top' | 'bottom'>();

  useEffect(() => {
    if (open && !autoFlip) {
      return () => {
        if (!queryRef.current) {
          preferredDropdownPlacement.current = undefined;
        }
      };
    }
  }, [autoFlip, open]);

  const dropdownContainerStyling = useMemo(() => {
    if (elemBoundingRect == null || !open) return undefined;

    let maxHeight =
      maxDropdownHeight == null
        ? viewport.height * 0.65 // default to 65% of viewport height
        : maxDropdownHeight > 0
        ? maxDropdownHeight
        : undefined; // no max height limit if set to zero explicitly.

    const baseStyle: StyleProp<ViewStyle> = [
      {
        position: 'absolute',
        width: matchWidth ? elemBoundingRect.width : undefined,
        maxWidth: matchWidth ? elemBoundingRect.width : undefined,
        maxHeight,
      },
      componentStyles.dropdownContainer,
    ];

    if (!dropdownHeight || !dropdownWidth) {
      return {
        dropdownContainerBaseStyle: baseStyle,
        dropdownContainerStyle: [
          baseStyle,
          { opacity: 0, left: -99999, top: -99999 },
        ],
        transformOrigin: undefined,
        originalTransformOrigin: undefined,
      };
    }

    const expectedDropdownWidth = matchWidth
      ? elemBoundingRect.width
      : dropdownWidth;

    const positionStyle: ViewStyle = {
      left: undefined,
      top: undefined,
      right: undefined,
      bottom: undefined,
    };

    if (align === 'left' || matchWidth) {
      positionStyle.left = elemBoundingRect.x;
    } else if (align === 'right') {
      positionStyle.right =
        boundingRect.width - elemBoundingRect.x - elemBoundingRect.width;
    } else {
      positionStyle.left = elemBoundingRect.x + elemBoundingRect.width / 2;
    }

    let webTransformOrigin: string | undefined = undefined;
    let placement: 'top' | 'bottom' = 'bottom';

    const transformOrigin = { x: expectedDropdownWidth / 2, y: 0 };
    const originalTransformOrigin = {
      x: expectedDropdownWidth / 2,
      y: dropdownHeight / 2,
    };

    if (
      (preferredDropdownPlacement.current == null &&
        elemBoundingRect.y + elemBoundingRect.height + dropdownHeight >
          boundingRect.y + boundingRect.height - kDefaultMargin) ||
      preferredDropdownPlacement.current === 'top'
    ) {
      positionStyle.bottom = viewport.height - elemBoundingRect.y;
      webTransformOrigin = 'bottom center';
      placement = 'top';
      transformOrigin.y = dropdownHeight;
      if (maxHeight != null) {
        maxHeight = Math.min(elemBoundingRect.y - saTop, maxHeight);
      }
    } else {
      positionStyle.top = elemBoundingRect.y + elemBoundingRect.height;
      webTransformOrigin = 'top center';
      placement = 'bottom';
      if (maxHeight != null) {
        maxHeight = Math.min(
          boundingRect.height -
            (elemBoundingRect.y + elemBoundingRect.height) -
            saBottom,
          maxHeight,
        );
      }
    }

    if (!preferredDropdownPlacement.current && !autoFlip) {
      preferredDropdownPlacement.current = placement;
    }

    return {
      dropdownContainerBaseStyle: [baseStyle, { maxHeight }],
      dropdownContainerStyle: [
        baseStyle,
        { maxHeight },
        positionStyle,
        Platform.OS === 'web'
          ? ({
              transformOrigin: webTransformOrigin,
            } as any)
          : undefined,
      ],
      webTransformOrigin,
      transformOrigin,
      originalTransformOrigin,
    };
  }, [
    align,
    autoFlip,
    boundingRect.height,
    boundingRect.width,
    boundingRect.y,
    componentStyles.dropdownContainer,
    dropdownHeight,
    dropdownWidth,
    elemBoundingRect,
    matchWidth,
    maxDropdownHeight,
    open,
    saBottom,
    saTop,
    viewport.height,
  ]);

  const animatedDropdownStyle = useAnimatedStyle(() => {
    const opacity = interpolate(animValue.value, [0, 1], [0, 1]);
    const scaleY = interpolate(animValue.value, [0, 1], [0, 1]);
    return {
      opacity,
      transform:
        Platform.OS === 'web'
          ? [{ scaleY }]
          : dropdownContainerStyling?.transformOrigin &&
            dropdownContainerStyling?.originalTransformOrigin
          ? (transformationsWithTransformOriginAnimated(
              dropdownContainerStyling?.transformOrigin,
              dropdownContainerStyling?.originalTransformOrigin,
              { scaleY } as any,
            ) as any)
          : undefined,
    };
  }, [
    dropdownContainerStyling?.transformOrigin,
    dropdownContainerStyling?.originalTransformOrigin,
    dropdownHeight,
  ]);

  const inputContainerStyle = useCallback(
    (focused: boolean) => [
      componentStyles.inputContainer,
      focused ? componentStyles.focusedInputContainer : undefined,
    ],
    [componentStyles.inputContainer, componentStyles.focusedInputContainer],
  );

  const inputStyle = useCallback(
    (focused: boolean) => [
      componentStyles.input,
      focused ? componentStyles.focusedInput : undefined,
      showCaretIcon ? { paddingRight: caretIconContainerWidth } : undefined,
      withIcon ? { paddingLeft: iconWidth } : undefined,
      withCustomValueRender ? { opacity: 0 } : undefined,
    ],
    [
      caretIconContainerWidth,
      componentStyles.focusedInput,
      componentStyles.input,
      showCaretIcon,
      withCustomValueRender,
      withIcon,
      iconWidth,
    ],
  );

  const valueWrapStyle = useMemo<StyleProp<ViewStyle>>(
    () => [
      StyleSheet.absoluteFill,
      showCaretIcon || withClearButton
        ? {
            paddingRight:
              (showCaretIcon ? caretIconContainerWidth : 0) +
              (withClearButton ? caretIconContainerWidth * 0.6 : 0),
          }
        : undefined,
      {
        justifyContent: 'center',
        paddingLeft:
          flattenMenuItemStyle?.paddingLeft ??
          flattenMenuItemStyle?.paddingHorizontal,
      },
    ],
    [
      caretIconContainerWidth,
      flattenMenuItemStyle?.paddingHorizontal,
      flattenMenuItemStyle?.paddingLeft,
      showCaretIcon,
      withClearButton,
    ],
  );

  const animatedIconStyle = useAnimatedStyle(() => {
    const rotate = interpolate(animValue.value, [0, 1], [180, 0]);
    return {
      transform: [
        {
          rotateZ: `${rotate}deg`,
        },
      ],
    };
  });

  const clearable = useMemo(() => {
    return {
      ...componentStyles.clearable,
      style: [
        componentStyles.clearable?.style,
        {
          right: showCaretIcon ? caretIconContainerWidth : 0,
        },
      ],
    };
  }, [caretIconContainerWidth, componentStyles.clearable, showCaretIcon]);

  const menuItemStyle = useMemo<StyleProp<ViewStyle>>(
    () => [
      componentStyles.menuItem,
      mode === 'multiple'
        ? {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }
        : undefined,
    ],
    [componentStyles.menuItem, mode],
  );

  return {
    container: componentStyles.container,
    menuItem: menuItemStyle,
    menuItemText: componentStyles.menuItemText,
    inputContainer: inputContainerStyle,
    focusedInputContainer: componentStyles.focusedInputContainer,
    input: inputStyle,
    valueWrap: valueWrapStyle,
    dropdownContainer: dropdownContainerStyling?.dropdownContainerStyle,
    dropdownContainerBaseStyle:
      dropdownContainerStyling?.dropdownContainerBaseStyle,
    animatedDropdownStyle,
    dropdown: componentStyles.dropdown,
    dropdownContentContainer: componentStyles.dropdownContentContainer,
    backdropMode: backdropMode ?? context?.backdropMode ?? 'transparent',
    backdrop: componentStyles.backdrop,
    backdropBlurTint: backdropBlurTint ?? context?.backdropBlurTint,
    backdropBlurIntensity:
      backdropBlurIntensity ?? context?.backdropBlurIntensity,
    animatedIconStyle,
    icon: componentStyles.icon,
    clearable,
    caretIcon: componentStyles.caretIcon,
    searchInput: componentStyles.searchInput,
    creatable: componentStyles.creatable,
    creatableInput: componentStyles.creatableInput,
    creatableTitle: componentStyles.creatableTitle,
    tag: componentStyles.tag,
    tagText: componentStyles.tagText,
    tagRemoveIcon: componentStyles.tagRemoveIcon,
    placeholderTextColor: placeholderTextColor ?? context?.placeholderTextColor,
    searchInputPlaceholderTextColor:
      searchInputPlaceholderTextColor ??
      context?.searchInputPlaceholderTextColor,
  };
};
